import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean
} from '/ezlibrary/helpers/EzHelpers.js';


import {
    EzRegistrationState,
    EzLogLevel
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';

import { EzDefaultLogConsole } from '/ezlibrary/logging/EzDefaultLogConsole.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * EzClocker's logger class.
 * There is no need to import this class if you reference it from ezApi.ezclocker.logger.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *      import { EzLogger } from '/ezlibrary/logging/EzLogger.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Wait for singleton instance ready:
 *      EzClass.ezWaitReady(
 *          EzLogger.ezEventNames.onReady,
 *          {Listening_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton instance ready when true:
 *     globalThis.ezApi.ezclocker?.[EzLogger.ezApiName]?.ready;
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton instance reference:
 *      From oustide this class: globalThis.ezApi.ezclocker.ezLogger
 *      From within this class: EzLogger.ezInstance
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzLogger extends EzClass {
    /**
     * @static
     * @private @field
     * Stores the reference to the console logs are written to
     * Default: new EzDefaultConsole()
     *
     * @type {object|EzDefaultLogConsole}
     */
    static #ezLogConsole = globalThis?.window?.console || new EzDefaultLogConsole();
    /**
     * @static
     * @public @property @getter
     * Gets the configured logging console.
     *
     * @returns {object|EzDefaultLogConsole}
     */
    static get ezLogConsole() {
        return this.#ezLogConsole;
    }
    /**
     * @static
     * @public @property @getter
     * Gets the configured logging console.
     *
     * @param {undefined|null|object|EzDefaultLogConsole} logConsole
     * Default: new EzDefaultConsole()
     */
    static set ezLogConsole(logConsole = globalThis?.window?.console || new EzDefaultLogConsole()) {
        this.#ezLogConsole = EzObject.assignOrDefault(
            logConsole,
            globalThis?.window?.console || new EzDefaultLogConsole());
    }
    /**
     * @public @property @getter
     * Gets the configured logging console.
     *
     * @returns {object|EzDefaultLogConsole}
     */
    get ezLogConsole() {
        return EzLogger.ezLogConsole();
    }
    /**
     * @public @property @getter
     * Gets the configured logging console.
     *
     * @param {undefined|null|object} logConsole
     * Default: new EzDefaultConsole()
     */
    set ezLogConsole(logConsole) {
        EzLogger.ezLogConsole = EzObject.assignOrDefault(
            logConsole,
            new EzDefaultLogConsole());
    }

    /**
     * @static
     * @private @field
     * Stores the current log level
     * @type {string}
     */
    static #ezLogLevel = EzLogLevel.DEBUG;
    /**
     * @static
     * @public @property @getter
     * Gets the currently logging level
     * @returns {string}
     * A valid enum property value from EzLogLevel
     */
    static get ezLogLevel() {
        return this.#ezLogLevel;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the logging level to use.
     * @param {string} aLogLevel
     * A valid enum property from EzLogLevel
     */
    static set ezLogLevel(aLogLevel) {
        if (!aLogLevel || 'string' !== typeof aLogLevel) {
            throw new EzBadParamException(
                'aLogLevel',
                EzLogger,
                'set ezLogLevel(logLevel)');
        }

        switch (EzLogLevel.ezAsEnum(aLogLevel)) {
            case EzLogLevel.ERROR:
                EzLogger.ezDebugEnabled = false;

                EzLogger.ezInfoEnabled = false;

                EzLogger.ezWarnEnabled = false;

                EzLogger.ezErrorEnabled = EzLogger.ezLoggingEnabled;

                break;
            case EzLogLevel.WARN:
                EzLogger.ezDebugEnabled = false;

                EzLogger.ezInfoEnabled = false;

                EzLogger.ezWarnEnabled = EzLogger.ezLoggingEnabled;

                EzLogger.ezErrorEnabled = EzLogger.ezLoggingEnabled;

                break;
            case EzLogLevel.INFO:
                EzLogger.ezDebugEnabled = false;

                EzLogger.ezInfoEnabled = EzLogger.ezLoggingEnabled;

                EzLogger.ezWarnEnabled = EzLogger.ezLoggingEnabled;

                EzLogger.ezErrorEnabled = EzLogger.ezLoggingEnabled;

                break;
            case EzLogLevel.DEBUG:
                EzLogger.ezDebugEnabled = EzLogger.ezLoggingEnabled;

                EzLogger.ezInfoEnabled = EzLogger.ezLoggingEnabled;

                EzLogger.ezWarnEnabled = EzLogger.ezLoggingEnabled;

                EzLogger.ezErrorEnabled = EzLogger.ezLoggingEnabled;

                break;
            default:
                throw new EzBadParamException(
                    'aLogLevel',
                    EzLogger,
                    'set ezLogLevel(logLevel)');
        }
    }
    /**
     * @public @property @getter
     * Gets the currently logging level
     * @returns {string}
     * A valid enum property value from EzLogLevel
     */
    get ezLogLevel() {
        return EzLogger.ezLogLevel;
    }
    /**
     * @public @property @setter
     * Sets the logging level to use.
     * @param {string} aLogLevel
     * A valid enum property from EzLogLevel
     */
    set ezLogLevel(aLogLevel = EzLogLevel.DEBUG) {
        EzLogger.ezLogLevel = aLogLevel;
    }

    /**
     * @static
     * @private @field
     * Stores if logging is enabled
     * @type {boolean}
     */
    static #ezLoggingEnabled = true;
    /**
     * @static
     * @public @property @setter
     * Gets if logging is enabled. The value returned only indicates that logging is enabled. However, is is possible for specific
     * logging levels to be disabled if set outside of the ezLoggingEnabled assignment.
     * @returns {boolean}
     */
    static get ezLoggingEnabled() {
        return EzObject.isValid(EzLogger.ezLogConsole) && this.#ezLoggingEnabled;
    }
    /**
     * @static
     * @public @property @setter
     * Sets if logging enabled. Will assign the provided ezLoggingEnabled value to all supported logging levels.
     * @param {boolean} ezLoggingEnabled
     * Default: true
     */
    static set ezLoggingEnabled(ezLoggingEnabled = true) {
        this.#ezLoggingEnabled = EzObject.isValid(EzLogger.ezLogConsole) && EzBoolean.booleanOrTrue(ezLoggingEnabled);

        EzLogger.ezDebugEnabled = EzLogger.#ezLoggingEnabled;

        EzLogger.ezWarnEnabled = EzLogger.#ezLoggingEnabled;

        EzLogger.ezInfoEnabled = EzLogger.#ezLoggingEnabled;

        EzLogger.ezErrorEnabled = EzLogger.#ezLoggingEnabled;
    }
    /**
     * @public @property @setter
     * Gets if logging is enabled. The value returned only indicates that logging is enabled. However, is is possible for specific
     * logging levels to be disabled if set outside of the ezLoggingEnabled assignment.
     * @returns {boolean}
     */
    get ezLoggingEnabled() {
        return EzLogger.ezLoggingEnabled;
    }
    /**
     * @public @property @setter
     * Sets if logging enabled. Will assign the provided ezLoggingEnabled value to all supported logging levels.
     * @param {boolean} ezLoggingEnabled
     * Default: true
     */
    set ezLoggingEnabled(ezLoggingEnabled = true) {
        EzLogger.ezLoggingEnabled = EzBoolean.booleanOrTrue(ezLoggingEnabled);
    }

    /**
     * @static
     * @private @field
     * Stores if debug logging is enabled
     * Default: true
     * @type {boolean}
     */
    static #ezDebugEnabled = true;
    /**
     * @static
     * @public @property @getter
     * Gets if debug logging is enabled
     * @returns {boolean}
     */
    static get ezDebugEnabled() {
        return EzLogger.ezLoggingEnabled && this.#ezDebugEnabled;
    }
    /**
     * @static
     * @public @property @setter
     * Enable or disable debug logging.
     * @param {boolean} debugEnabled
     * Default: true
     */
    static set ezDebugEnabled(debugEnabled = true) {
        this.#ezDebugEnabled = EzBoolean.booleanOrTrue(debugEnabled);
    }
    /**
     * @public @property @getter
     * Gets if debug logging is enabled
     * @returns {boolean}
     */
    get ezDebugEnabled() {
        return EzLogger.ezDebugEnabled;
    }
    /**
     * @public @property @setter
     * Enable or disable debug logging.
     * @param {boolean} debugEnabled
     * Default: true
     */
    set ezDebugEnabled(debugEnabled = true) {
        EzLogger.ezDebugEnabled = EzBoolean.booleanOrTrue(debugEnabled);
    }

    /**
     * @static
     * @private @field
     * Stores if info logging is enabled
     * Default: true
     * @type {boolean}
     */
    static #ezInfoEnabled = true;
    /**
     * @static
     * @public @property @getter
     * Gets if info logging is enabled
     * @returns {boolean}
     */
    static get ezInfoEnabled() {
        return EzLogger.ezLoggingEnabled && this.#ezInfoEnabled;
    }
    /**
     * @static
     * @public @property @setter
     * Enable or disable info logging.
     * @param {boolean} ezInfoEnabled
     * Default: true
     */
    static set ezInfoEnabled(ezInfoEnabled = true) {
        this.#ezInfoEnabled = EzBoolean.booleanOrTrue(ezInfoEnabled);
    }
    /**
     * @public @property @getter
     * Gets if info logging is enabled
     * @returns {boolean}
     */
    get ezInfoEnabled() {
        return EzLogger.ezInfoEnabled;
    }
    /**
     * @public @property @setter
     * Enable or disable info logging.
     * @param {boolean} ezInfoEnabled
     * Default: true
     */
    set ezInfoEnabled(ezInfoEnabled = true) {
        EzLogger.ezInfoEnabled = EzBoolean.booleanOrTrue(ezInfoEnabled);
    }

    /**
     * @static
     * @private @field
     * Stores if warn logging is enabled
     * Default: true
     * @type {boolean}
     */
    static #ezWarnEnabled = true;
    /**
     * @public @property @getter
     * Gets if warn logging is enabled
     * @returns {boolean}
     */
    static get ezWarnEnabled() {
        return EzLogger.ezLoggingEnabled && this.#ezWarnEnabled;
    }
    /**
     * @public @property @setter
     * Enable or disable warn logging.
     * @param {boolean} warnEnabled
     * Default: true
     */
    static set ezWarnEnabled(warnEnabled = true) {
        this.#ezWarnEnabled = EzBoolean.booleanOrTrue(warnEnabled);
    }
    /**
     * @public @property @getter
     * Gets if warn logging is enabled
     * @returns {boolean}
     */
    get ezWarnEnabled() {
        return EzLogger.ezWarnEnabled;
    }
    /**
     * @public @property @setter
     * Enable or disable warn logging.
     * @param {boolean} warnEnabled
     * Default: true
     */
    set ezWarnEnabled(warnEnabled = true) {
        EzLogger.ezWarnEnabled = EzBoolean.booleanOrTrue(warnEnabled);
    }

    /**
     * @static
     * @private @field
     * Stores if error logging is enabled
     * Default: true
     * @type {boolean}
     */
    static #ezErrorEnabled = true;
    /**
     * @static
     * @public @property @getter
     * Gets if error logging is enabled
     * @returns {boolean}
     */
    static get ezErrorEnabled() {
        return this.#ezErrorEnabled;
    }
    /**
     * @static
     * @public @property @setter
     * Enable or disable error logging.
     * @param {boolean} errorEnabled
     * Default: true
     */
    static set ezErrorEnabled(errorEnabled = true) {
        this.#ezErrorEnabled = EzBoolean.booleanOrTrue(errorEnabled);
    }/**
     * @public @property @getter
     * Gets if error logging is enabled
     * @returns {boolean}
     */
    get ezErrorEnabled() {
        return EzLogger.ezErrorEnabled;
    }
    /**
     * @public @property @setter
     * Enable or disable error logging.
     * @param {boolean} errorEnabled
     * Default: true
     */
    set ezErrorEnabled(errorEnabled = true) {
        EzLogger.ezErrorEnabled = EzBoolean.booleanOrTrue(errorEnabled);
    }

    /**
     * @static
     * @public @property @getter
     * Gets if logging is enabled
     * @returns {boolean}
     */
    static get enabled() {
        return EzObject.isValid(EzLogger?.ezLogConsole) && EzBoolean.booleanOrFalse(EzLogger?.ezLoggingEnabled);
    }
    /**
     * @public @property @getter
     * Gets if logging is enabled
     * @returns {boolean}
     */
    get enabled() {
        return EzLogger.enabled;
    }

    /**
     * @static
     * @public @method
     * Writes a deprecation log message
     * @param {string} depItem
     * @param {string|null} migrateTo
     * @param {string|null} msg
     * @param {string|null} source
     */
    static deprecate(depItem, useInstead, msg, source) {
        if (!EzLogger.ezEnabled) {
            return;
        }

        EzLogger.dep(depItem, useInstead, msg, source);
    }
    /**
     * @public @method
     * Writes a deprecation log message
     * @param {string} depItem
     * @param {string|null} migrateTo
     * @param {string|null} msg
     * @param {string|null} source
     */
    deprecate = EzLogger.deprecate;

    /**
     * @static
     * @public @method
     * Attempts to log a useable message from an exception catch err object.
     * @param {string} em
     * @param {object|null} err
     */
    static ezLogException(em, err) {
        if (!EzLogger.ezEnabled) {
            return;
        }

        if (em && 'string' === typeof em && 0 < em.length) {
            let exMsg = `EzClocker Exception: ${em} (no additional details).`;

            if (err.message) {
                exMsg = `${exMsg}, Details: ${err.message}`;
            }

            if (err.stack) {
                exMsg = `${exMsg}, Stack trace: ${err.stack}`;
            }

            if (err.stderr && 0 < err.stderr.length) {
                exMsg = `${exMsg} -> ${err.stderr}`;
            }

            EzLogger.error(exMsg);
        } else if (err) {
            EzLogger.error(err);
        }
    }
    /**
     * @public @method
     * Attempts to log a useable message from an exception catch err object.
     * @param {string} em
     * @param {object|null} err
     */
    ezLogException = EzLogger.ezLogException;

    /**
     * @static
     * @public @method
     * Logs a deprecation message in the format:
     *      `Use of ${depItem} is deprecated and no longer supported. ${migrateTo} ${!msg || 'string' !== typeof msg || 0 === msg.length ? '' : msg}`,
     * @param {string} depItem
     * @param {string|null} migrateTo
     * @param {string|null} msg
     * @param {string|null} source
     */
    static dep(depItem, migrateTo, msg, source) {
        if (!EzLogger.ezEnabled || !depItem || 'string' !== typeof depItem || 0 === depItem.length) {
            // Logger not enabled
            return;
        }

        EzLogger.warn(
            `Use of ${depItem} is deprecated and no longer supported. ` +
            `${!migrateTo || 'string' !== typeof migrateTo || 0 === migrateTo.length ? '' : `Migrate to ${migrateTo}. `} ` +
            `${!msg || 'string' !== typeof msg || 0 === msg.length ? '' : msg}`,
            source);
    }
    /**
     * @public @method
     * Logs a deprecation message in the format:
     *      `Use of ${depItem} is deprecated and no longer supported. ${migrateTo} ${!msg || 'string' !== typeof msg || 0 === msg.length ? '' : msg}`,
     *
     * @param {string} depItem
     * @param {string|null} migrateTo
     * @param {string|null} msg
     * @param {string|null} source
     */
    dep = EzLogger.dep;

    /**
     * @static
     * @public @method
     * Logs a message at the error level
     *
     * @param {string|null} msg
     * @param {string|null} source
     */
    static error(msg, source) {
        if (!EzLogger.ezErrorEnabled || !msg || 'string' !== typeof msg || 0 === msg.length) {
            return;
        }

        EzLogger.ezLogConsole.error(
            `${!source || 'string' !== typeof source || 0 === source.length ? '' : `[${source}]: `}${msg}`);
    }
    /**
     * @public @method
     * Logs a message at the error level
     *
     * @param {string|null} msg
     * @param {string|null} source
     */
    error = EzLogger.error;

    /**
     * @static
     * @public @method
     * Logs a message at the error level
     * @param {string} msg
     * @param {string} source
     */
    static warn(msg, source) {
        if (!EzLogger.ezWarnEnabled || !msg || 'string' !== typeof msg || 0 === msg.length) {
            return;
        }

        EzLogger.ezLogConsole.warn(`${!source || 'string' !== typeof source || 0 === source.length ? '' : `[${source}]: `}${msg}`);
    }
    /**
     * @public @method
     * Logs a message at the error level
     *
     * @param {string} msg
     * @param {string} source
     */
    warn = EzLogger.warn;

    /**
     * @static
     * @public @method
     * Logs a message at the error level
     *
     * @param {string|null} msg
     * @param {string|null} source
     */
    static info(msg, source) {
        if (!EzLogger.ezInfoEnabled || !msg || 'string' !== typeof msg || 0 === msg.length) {
            return;
        }

        EzLogger.ezLogConsole.info(`${!source || 'string' !== typeof source || 0 === source.length ? '' : `[${source}]: `}${msg}`);
    }
    /**
     * @public @method
     * Logs a message at the error level
     * @param {string|null} msg
     * @param {string|null} source
     */
    info(msg, source) {
        return EzLogger.info(msg, source);
    }

    /**
     * @static
     * @public @method
     * Logs a message at the error level
     * @param {string|null} msg
     * @param {string|null} source
     */
    static debug(msg, source) {
        if (!EzLogger.ezDebugEnabled || !msg || 'string' !== typeof msg || 0 === msg.length) {
            return;
        }

        EzLogger.ezLogConsole.debug(`${!source || 'string' !== typeof source || 0 === source.length ? '' : `[${source}]: `}${msg}`);
    }
    /**
     * @public @method
     * Logs a message at the error level
     * @param {string|null} msg
     * @param {string|null} source
     */
    debug = EzLogger.debug;

    /**
     * @static
     * @public @function
     * Creates a console log group
     * @param {string|null} logGroupName
     * Default: '[EzClocker]'
     */
    static startLogGroup(logGroupName = '[EzClocker]') {
        if (!EzLogger.ezEnabled) {
            return;
        }

        EzLogger.ezLogConsole.group(
            EzString.stringWithTextOrDefault(
                logGroupName,
                '[EzClocker]'));
    }

    /**
     * @public @function
     * Creates a console log group
     * @param {string|null} logGroupName
     * Default: '[EzClocker]'
     */
    startLogGroup = EzLogger.startLogGroup;

    /**
     * @static
     * @public @function
     * Ends a console log group
     * @param {string|null} logGroupName
     * Default: '[EzClocker]'
     */
    static endLogGroup() {
        if (!EzLogger.ezEnabled) {
            return;
        }

        EzLogger.ezLogConsole.groupEnd();
    }

    /**
     * @public @function
     * Ends a console log group
     * @param {string|null} logGroupName
     * Default: '[EzClocker]'
     */
    endLogGroup = EzLogger.endLogGroup;

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @public @constructor
     * Creates a new instance of EzLogger
     * @param {undefined|null|object} logConsoleToUse
     */
    constructor(logConsoleToUse = globalThis?.console) {
        super();

        EzLogger.ezLogConsole = logConsoleToUse || globalThis.console || new EzDefaultConsole();

        EzLogger.ready = true;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezLogger';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the alternative 'short' name of this class's singleton instance when registered with ezApi.
     * @returns {string}
     */
    static get ezShortApiName() {
        return 'logger';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzLogger_Ready'
        };
    }

    /**
     * @static
     * @public @getter @property
     * Gets the singleton instance of EzDateTime registered with EzApi (if available yet)
     * @param {EzLogger}
     */
    static get ezInstance() {
        return EzLogger;
    }

    /**
     * @static
     * @public @field
     * Stores the EzApiRegistrationState of EzLogger's singleton instance.
     * @type {String}
     * A valid enumeration property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzLogger.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzLogger.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzLogger.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if all necessary dependences are ready and therefore it is ok for this class to
     * register it's singleton instance with ezApi.
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzLogger.ezApiRegistrationState &&
            globalThis?.ezApi?.ready && globalThis.ezApi?.ezclocker;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return EzRegistrationState.REGISTERED === EzLogger.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with ezApi.
     * @param {undefined|null|EzLogger} ezLoggerInstance
     * Default: null
     * @returns {boolean}
     * Returns true if successful, false otherwise.
     */
    static #ezRegistrator() {
        if (EzLogger.#ezCanRegister && !EzLogger.#ezIsRegistered) {
            EzLogger.ready = true;

            globalThis.ezApi.ezclocker[EzLogger.ezShortApiName] = EzLogger;

            globalThis.ezApi.ezclocker[EzLogger.ezApiName] = EzLogger;

            globalThis[EzLogger.ezApiName] = EzLogger;

            // EzLogger is unique in how it registers because it is created and registerd in the EzApi class constructor.
            EzLogger.ezApiRegistrationState = EzRegistrationState.REGISTERED;

            document.dispatchEvent(
                new CustomEvent(
                    EzLogger.ezEventNames.onReady,
                    {
                        bubbles: true
                    }));
        }

        return EzRegistrationState.REGISTERED === EzLogger.ezApiRegistrationState;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzLogger.ezApiName
     *     2) Property getter EzLogger.ezEventNames
     *     3) Property getter EzLogger.ezInstance
     *     4) Property setter EzLogger.ezInstance
     *     5) Property getter EzLogger.ezApiRegistrationState
     *     6) Property setter EzLogger.ezApiRegistrationState
     *     7) Property getter EzLogger.#ezCanRegister
     *     8) Property getter EzLogger.#ezIsRegistered
     *     9) Method EzLogger.#ezRegistrator()
     */
    static {
        if (!EzLogger.#ezIsRegistered) {
            EzLogger.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzLogger.#ezRegistrator(globalThis?.[EzLogger.ezApiName])) {
                EzClass.ezWaitReady(
                    EzLogger.ezOnEzApiReadyEventName,
                    () => EzLogger.#ezRegistrator(globalThis?.[EzLogger.ezApiName]));
            }
        }
    }
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}

globalThis[EzLogger.ezApiName] = EzLogger;
